/**
 * Cushion Charts Component - Display charts for cushion/foam production data
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  ReferenceLine,
  Legend
} from 'recharts';
import { 
  Activity, 
  TrendingUp, 
  Users, 
  Package,
  Scissors,
  Wrench,
  Layers,
  Zap
} from 'lucide-react';
import { DailyCushionData, CushionChartData } from '@/types/cushion';
import { formatNumberDisplay } from '@/lib/utils';
import { getCushionChartData } from '@/integrations/sqlserver/cushionService';

interface CushionChartsProps {
  dailyData: DailyCushionData[];
  isLoading?: boolean;
  error?: string | null;
  processes?: string[];
  title?: string;
}

// Process configurations for cushion production
const processColors = {
  catVai: '#3b82f6',    // Blue - Cắt vải
  may: '#10b981',       // Green - May
  thanhPham: '#f59e0b', // Amber - Thành phẩm
  doMut: '#0d9488',     // Teal - Đổ mút
  catMut: '#ef4444',    // Red - Cắt mút
  gon: '#6b7280',       // Gray - Gòn
};

const processNames = {
  catVai: 'Cắt vải',
  may: 'May',
  thanhPham: 'Thành phẩm',
  doMut: 'Đổ mút',
  catMut: 'Cắt mút',
  gon: 'Gòn',
};

export const CushionCharts: React.FC<CushionChartsProps> = ({ 
  dailyData, 
  isLoading = false, 
  error = null,
  processes = ['catVai', 'may', 'thanhPham', 'doMut', 'catMut', 'gon'],
  title = "Biểu đồ sản xuất nệm mút"
}) => {
  const [activeMetric, setActiveMetric] = useState<'labor' | 'volume' | 'productivity'>('volume');

  // Get chart data based on selected metric and processes
  const chartData = useMemo(() => {
    if (dailyData.length === 0) return [];
    const fullChartData = getCushionChartData(dailyData, activeMetric);
    
    // Filter data to only include selected processes
    return fullChartData.map(item => {
      const filteredItem: any = { date: item.date };
      processes.forEach(process => {
        filteredItem[process] = item[process as keyof typeof item] || 0;
      });
      return filteredItem;
    });
  }, [dailyData, activeMetric, processes]);

  // Calculate average trendline values (only for working days)
  const averageValues = useMemo(() => {
    if (chartData.length === 0) return {};
    
    // Define working days based on selected processes activity
    const workingDaysData = chartData.filter(day => {
      const totalActivity = processes.reduce((sum, process) => sum + (day[process] || 0), 0);
      return totalActivity > 0;
    });

    if (workingDaysData.length === 0) return {};

    const averages: Record<string, number> = {};

    processes.forEach(process => {
      // Calculate average including zero values for the specific metric on working days
      const total = workingDaysData.reduce((sum, day) => sum + (day[process] || 0), 0);
      averages[process] = total / workingDaysData.length;
    });

    return averages;
  }, [chartData, processes]);

  // Format number with appropriate decimals
  // Use Vietnamese locale formatting for numbers

  // Get unit for current metric (dynamic based on process)
  const getUnit = (processKey?: string) => {
    switch (activeMetric) {
      case 'labor': return 'Công';
      case 'volume': 
        return processKey === 'gon' ? 'Kg' : 'M³';
      case 'productivity': 
        return processKey === 'gon' ? 'Kg/Công' : 'M³/Công';
      default: return '';
    }
  };

  // Get title for current metric
  const getTitle = () => {
    switch (activeMetric) {
      case 'labor': return 'Công lao động';
      case 'volume': {
        // Check if Gòn is in the processes - if so, show mixed title
        const hasGon = processes.includes('gon');
        const hasOthers = processes.some(p => p !== 'gon');
        if (hasGon && hasOthers) return 'M³ Công suất / Kg Gòn';
        if (hasGon && !hasOthers) return 'Kg Công suất';
        return 'M³ Công suất';
      }
      case 'productivity': return 'Năng suất';
      default: return '';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            <Skeleton className="h-6 w-48" />
          </CardTitle>
          <CardDescription>
            <Skeleton className="h-4 w-64" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-9 w-24" />
              ))}
            </div>
            <Skeleton className="h-80 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-red-500" />
            Biểu đồ sản xuất nệm mút
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-600">
            <p>Lỗi tải biểu đồ: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Biểu đồ sản xuất nệm mút
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Không có dữ liệu để hiển thị biểu đồ</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Title Section */}
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              📈 {title}
            </CardTitle>
            <CardDescription className="mt-1">
              Xu hướng {getTitle().toLowerCase()} các công đoạn sản xuất nệm mút theo thời gian
            </CardDescription>
          </div>
          
          {/* Metric Selection Buttons - Top Right on Desktop, Centered on Mobile */}
          <div className="flex flex-wrap gap-2 justify-center lg:justify-end">
            <Button
              variant={activeMetric === 'labor' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('labor')}
              className="flex items-center gap-1"
            >
              <Users className="h-4 w-4" />
              Công lao động
            </Button>
            
            <Button
              variant={activeMetric === 'volume' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('volume')}
              className={`flex items-center gap-1 ${
                activeMetric === 'volume' 
                  ? 'bg-amber-500 hover:bg-amber-600' 
                  : 'border-amber-300 text-amber-700 hover:bg-amber-50'
              }`}
            >
              <Activity className="h-4 w-4" />
              {(() => {
                const hasGon = processes.includes('gon');
                const hasOthers = processes.some(p => p !== 'gon');
                if (hasGon && !hasOthers) return 'Kg Công suất';
                if (hasGon && hasOthers) return 'M³/Kg Công suất';
                return 'M³ Công suất';
              })()}
            </Button>
            
            <Button
              variant={activeMetric === 'productivity' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('productivity')}
              className="flex items-center gap-1"
            >
              <Zap className="h-4 w-4" />
              Năng suất
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">

          {/* Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('vi-VN', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                />
                <YAxis 
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={(value) => formatNumberDisplay(value, 0)}
                />
                <Tooltip 
                  formatter={(value: any, name: string) => [
                    `${formatNumberDisplay(value, 2)} ${getUnit(name)}`, 
                    processNames[name as keyof typeof processNames] || name
                  ]}
                  labelFormatter={(label) => 
                    new Date(label).toLocaleDateString('vi-VN', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })
                  }
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Legend
                  formatter={(value) => processNames[value as keyof typeof processNames] || value}
                  wrapperStyle={{
                    paddingTop: '16px',
                    fontSize: '14px'
                  }}
                />
                
                {/* Data lines for selected processes */}
                {processes.map((processKey) => (
                  <Line
                    key={processKey}
                    type="monotone"
                    dataKey={processKey}
                    stroke={processColors[processKey as keyof typeof processColors]}
                    strokeWidth={2}
                    dot={{ fill: processColors[processKey as keyof typeof processColors], strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, stroke: processColors[processKey as keyof typeof processColors], strokeWidth: 2 }}
                    connectNulls={false}
                  />
                ))}

                {/* Average trendlines for selected processes */}
                {processes.map((processKey) => {
                  const avgValue = averageValues[processKey];
                  if (avgValue === undefined) return null;
                  
                  return (
                    <ReferenceLine
                      key={`avg-${processKey}`}
                      y={avgValue}
                      stroke={processColors[processKey as keyof typeof processColors]}
                      strokeDasharray="5 5"
                      strokeOpacity={0.6}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};